import { isActiveOrg } from "lib/reauth/Form";
import OrgSelect from "lib/reauth/OrgSelect";
import { usePerms } from "lib/reauth/ReauthModule";
import PropTypes from "prop-types";
import React from "react";

const AncestryContext = React.createContext(undefined);

function isActiveAncestor(name) {
    const ancestry = React.useContext(AncestryContext);
    return ancestry && ancestry.has(name);
}

function Perms({
                   org,
                   onPermsChange,
               }) {
    const [editing, setEditing] = React.useState(false);
    const [modifiersActive, setModifiersActive] = React.useState(0);
    const perms = usePerms();
    if (!editing) {
        return <span className={"perms"}>
            ( <ul>
                {org.perms && org.perms.length
                    ? org.perms.map(p => <li key={p}>{p}</li>)
                    : <li>ALL</li>}
            </ul>
            {onPermsChange && <button
                onClick={() => setEditing(true)}
                title={"Edit license grants"}
            >
                ✐
            </button>} )
        </span>;
    }

    function handleKeyEvent(e, delta) {
        if (e.key === "Control" || e.key === "Shift" || e.key === "Alt") {
            setModifiersActive(n => n + delta);
        }
    }

    function handleChange(e) {
        let newPerms = Array.from(
            e.target.selectedOptions,
            option => option.value,
        );
        if (newPerms.length === 0) {
            newPerms = undefined;
        } else if (newPerms) {
            newPerms = [...(new Set(newPerms))];
            // ALL, ACCESS, and NONE are special...
            if (modifiersActive > 0 && newPerms.length > 1) {
                newPerms = newPerms.filter(p => p !== "NONE" && p !== "ALL" && p !== "ACCESS");
                if (newPerms.length === 0) {
                    newPerms = undefined;
                }
            } else if (newPerms.some(p => p === "NONE")) {
                newPerms = ["NONE"];
                if (modifiersActive === 0) {
                    setEditing(false);
                }
            } else if (newPerms.some(p => p === "ALL")) {
                newPerms = undefined;
                if (modifiersActive === 0) {
                    setEditing(false);
                }
            } else if (newPerms.length > 1) {
                newPerms = newPerms.filter(p => p !== "ACCESS");
            }
        }
        onPermsChange(newPerms);
    }

    return <span className={"perms"}>
        <select
            value={org.perms || ["ALL"]}
            autoFocus
            multiple
            size={perms.length}
            onKeyDown={e => handleKeyEvent(e, 1)}
            onKeyUp={e => handleKeyEvent(e, -1)}
            onChange={handleChange}
            onBlur={() => setEditing(false)}
        >
            {perms.map(p =>
                <option key={p} value={p}>{p}</option>)}
        </select>
    </span>;
}

const orgType = PropTypes.shape({
    name: PropTypes.string.isRequired,
    kids: PropTypes.array.isRequired,
});
Perms.propTypes = {
    org: orgType.isRequired,
    onPermsChange: PropTypes.func,
};

function Org({
                 org,
                 ancestors,
                 onKidsChange,
                 onPermsChange,
                 onDelete,
             }) {
    const active = isActiveOrg(org.name);
    const ancestor = isActiveAncestor(org.name);
    const selfAndAncestors = React.useMemo(
        () => (ancestors || []).concat(org.name),
        [org.name, ancestors],
    );

    return <li
        className={active ? "org active" : ancestor ? "org ancestor" : "org"}
    >
        <div
            className={"flex"}
            style={{justifyContent: "space-between"}}
        >
            <span
                className={"flex"}
            >
                {org.name}
                <Perms
                    org={org}
                    onPermsChange={onPermsChange}
                />
            </span>
            <span className={"flex"}>
                {onDelete && <button
                    onClick={onDelete}
                    title={"Remove this license"}
                >
                    ×
                </button>}
                <OrgSelect
                    excludes={selfAndAncestors}
                    allowBlank
                    title={"Create a new child license"}
                    value={""}
                    onChange={e =>
                        onKidsChange(org.kids.concat({
                            name: e.target.value,
                            kids: [],
                        }))}
                />
            </span>
        </div>
        <Kids
            kids={org.kids}
            ancestors={selfAndAncestors}
            onChange={onKidsChange}
        />
    </li>;
}

Org.propTypes = {
    org: orgType.isRequired,
    ancestors: PropTypes.arrayOf(PropTypes.string),
    onKidsChange: PropTypes.func.isRequired,
    onPermsChange: PropTypes.func,
    onDelete: PropTypes.func,
};

function augmentIth(arr, i, augment) {
    const next = arr.slice();
    next[i] = {
        ...next[i],
        ...augment,
    };
    return next;
}

function Kids({kids, ancestors, onChange}) {
    if (!kids || !kids.length) return null;
    return <ul>
        {kids.map((k, i) =>
            <Org
                key={k.name}
                org={k}
                ancestors={ancestors}
                onKidsChange={newKids =>
                    onChange(augmentIth(kids, i, {
                        kids: newKids,
                    }))}
                onPermsChange={newPerms =>
                    onChange(augmentIth(kids, i, {
                        perms: newPerms,
                    }))}
                onDelete={e => {
                    e.preventDefault();
                    const next = kids.slice();
                    next.splice(i, 1);
                    onChange(next);
                }}
            />)}
    </ul>;
}

Kids.propTypes = {
    kids: PropTypes.arrayOf(orgType),
    ancestors: PropTypes.arrayOf(PropTypes.string),
    onChange: PropTypes.func.isRequired,
};

function Forest({
                    forest,
                    ancestry,
                    onChange,
                    buildImplLicense,
                }) {
    return <ul>
        {forest.map((it, i) => {
            const handleKidsChange = newKids =>
                onChange(f => augmentIth(f, i, {
                    kids: newKids,
                }));
            const handleImplLicense = implementer =>
                handleKidsChange(
                    it.kids.concat(buildImplLicense(implementer))
                );
            return <li
                key={it.name}
                className={"impl"}
            >
                <div
                    className={"flex"}
                    style={{ justifyContent: "space-between" }}
                >
                                <span>
                                    {it.name} ({it.implementer})
                                </span>
                    {(!it.kids || it.kids.length === 0) && <button
                        title={"Create root license"}
                        onClick={e => {
                            e.preventDefault();
                            handleImplLicense(it.implementer);
                        }}
                    >
                        license
                    </button>}
                </div>
                <AncestryContext.Provider value={ancestry[it.name]}>
                    <Kids
                        kids={it.kids}
                        onChange={handleKidsChange}
                    />
                </AncestryContext.Provider>
            </li>;
        })}
    </ul>;
}

Forest.propTypes = {
    forest: PropTypes.array.isRequired,
    ancestry: PropTypes.object.isRequired,
    onChange: PropTypes.func.isRequired,
    buildImplLicense: PropTypes.func.isRequired,
};

export default Forest;



// WEBPACK FOOTER //
// ./app/lib/reauth/Forest.js